// src/components/resources/tools/generators/CommentThread/InstagramComments.tsx

'use client'

import type { FC } from 'react'
import { Heart } from 'lucide-react'
import { clsx } from 'clsx'
import { displayedLikes, formatCompactCount, normalizeHandle, type ThreadComment } from './threadModel'
import { CommentAvatar, likeButtonLabel, VerifiedCheck, type CommentPreviewProps } from './previewShared'

/**
 * Instagram comments sheet, styled to the current mobile UI: centered
 * "Comments" header, bold username inline with the comment text, a muted
 * `time · likes · Reply` meta row, the small outline heart on the right, and
 * one indent level of replies. Creator comments carry Instagram's "Author"
 * tag; pinned comments show the muted "Pinned" label above the username row.
 * The heart is a real toggle (aria-pressed): clicking it fills Instagram's
 * like red and bumps the displayed like count by one.
 */
export const InstagramComments: FC<CommentPreviewProps> = ({ comments, theme, onToggleLike }) => {
  const isDark = theme === 'dark'
  const text = isDark ? 'text-[#f5f5f5]' : 'text-gray-900'
  const muted = isDark ? 'text-[#a8a8a8]' : 'text-gray-500'
  const border = isDark ? 'border-[#262626]' : 'border-gray-200'

  const renderRow = (comment: ThreadComment, isReply = false): JSX.Element => (
    <div className={clsx('flex gap-3 py-2.5', isReply ? 'pl-11 pr-4' : 'px-4')}>
      <CommentAvatar username={comment.username} size={isReply ? 'sm' : 'md'} flavor="instagram" />
      <div className="min-w-0 flex-1">
        {comment.pinned && !isReply && (
          <p className={clsx('mb-0.5 text-[11px] font-medium', muted)}>Pinned</p>
        )}
        <p className={clsx('text-[13px] leading-snug', text)}>
          <span className="mr-1 inline-flex items-center gap-1 font-semibold">
            {normalizeHandle(comment.username)}
            {comment.verified && <VerifiedCheck className="text-[#0095f6]" />}
            {comment.byAuthor && (
              <span className={clsx('rounded border px-1 py-px text-[10px] font-medium', border, muted)}>
                Author
              </span>
            )}
          </span>
          {comment.text}
        </p>
        <p className={clsx('mt-1 flex items-center gap-3 text-[12px] font-medium', muted)}>
          <span>{comment.timestamp}</span>
          {displayedLikes(comment) > 0 && (
            <span>
              {formatCompactCount(displayedLikes(comment))} {displayedLikes(comment) === 1 ? 'like' : 'likes'}
            </span>
          )}
          <span>Reply</span>
        </p>
      </div>
      <button
        type="button"
        onClick={() => onToggleLike?.(comment.id)}
        aria-pressed={comment.liked}
        aria-label={likeButtonLabel(comment.liked, comment.username)}
        className="mt-1 flex-shrink-0 rounded p-0.5 transition-transform hover:scale-110"
      >
        <Heart className={clsx('h-3.5 w-3.5', comment.liked ? 'fill-[#ed4956] text-[#ed4956]' : muted)} />
      </button>
    </div>
  )

  return (
    <div className={clsx('overflow-hidden rounded-xl border shadow-xl', border, isDark ? 'bg-black' : 'bg-white')}>
      <div className={clsx('border-b py-2.5 text-center text-[13px] font-semibold', border, text)}>Comments</div>
      <div className="py-1">
        {comments.map(comment => (
          <div key={comment.id}>
            {renderRow(comment)}
            {comment.replies.map(reply => (
              <div key={reply.id}>{renderRow(reply, true)}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
